import { getRemoteConfig, fetchAndActivate, getValue } from 'firebase/remote-config';
import type { RemoteConfig } from 'firebase/remote-config';
import { CAPTURE_DEFAULTS, SCORE_WEIGHTS, THRESHOLDS_VERSION } from '@erectus/shared';
import type { ScoreWeights } from '@erectus/shared';
import { firebase } from './firebase.js';
import { isDev } from './env.js';

/**
 * Remote Config keys (docs/ARCHITECTURE.md §2.7). Defaults mirror the pinned
 * constants in @erectus/shared so the app is fully functional offline.
 */
export interface AppConfig {
  poseModel: 'lite' | 'full';
  sampleFps: number;
  scoreWeights: ScoreWeights;
  thresholdsVersion: string;
  classifierModelVersion: string | null;
  classifierRolloutPct: number;
  reminderDefaultDays: 3 | 7;
  llmProvider: 'gemini' | 'claude';
}

const defaults: AppConfig = {
  poseModel: CAPTURE_DEFAULTS.poseModel,
  sampleFps: CAPTURE_DEFAULTS.sampleFps,
  scoreWeights: SCORE_WEIGHTS,
  thresholdsVersion: THRESHOLDS_VERSION,
  classifierModelVersion: null,
  classifierRolloutPct: 0,
  reminderDefaultDays: 7,
  llmProvider: 'gemini',
};

let config: AppConfig = defaults;
let rc: RemoteConfig | null = null;
let loading: Promise<AppConfig> | null = null;

export function appConfig(): AppConfig {
  return config;
}

function num(raw: string, fallback: number): number {
  const n = Number(raw);
  return Number.isFinite(n) ? n : fallback;
}

export async function loadRemoteConfig(): Promise<AppConfig> {
  if (loading) return loading;
  loading = (async () => {
    try {
      rc = getRemoteConfig(firebase().app);
      rc.settings.minimumFetchIntervalMillis = isDev ? 0 : 60 * 60 * 1000;
      rc.defaultConfig = {
        pose_model: defaults.poseModel,
        sample_fps: String(defaults.sampleFps),
        score_weights: JSON.stringify(defaults.scoreWeights),
        thresholds_version: defaults.thresholdsVersion,
        classifier_model_version: '',
        classifier_rollout_pct: '0',
        reminder_default_days: '7',
        llm_provider: 'gemini',
      };
      await fetchAndActivate(rc);

      const poseModelRaw = getValue(rc, 'pose_model').asString();
      const weightsRaw = getValue(rc, 'score_weights').asString();
      let scoreWeights = defaults.scoreWeights;
      try {
        const parsed = JSON.parse(weightsRaw) as Partial<ScoreWeights>;
        scoreWeights = { ...defaults.scoreWeights, ...parsed };
      } catch {
        scoreWeights = defaults.scoreWeights;
      }
      const classifierVersion = getValue(rc, 'classifier_model_version').asString();
      const days = num(getValue(rc, 'reminder_default_days').asString(), 7);

      config = {
        poseModel: poseModelRaw === 'full' ? 'full' : 'lite',
        sampleFps: Math.min(
          CAPTURE_DEFAULTS.maxSampleFps,
          Math.max(
            CAPTURE_DEFAULTS.minSampleFps,
            num(getValue(rc, 'sample_fps').asString(), defaults.sampleFps),
          ),
        ),
        scoreWeights,
        thresholdsVersion:
          getValue(rc, 'thresholds_version').asString() || defaults.thresholdsVersion,
        classifierModelVersion: classifierVersion === '' ? null : classifierVersion,
        classifierRolloutPct: num(getValue(rc, 'classifier_rollout_pct').asString(), 0),
        reminderDefaultDays: days === 3 ? 3 : 7,
        llmProvider: getValue(rc, 'llm_provider').asString() === 'claude' ? 'claude' : 'gemini',
      };
    } catch {
      // Offline or Remote Config unavailable: pinned defaults are correct behaviour.
      config = defaults;
    }
    return config;
  })();
  return loading;
}
